import React, { useEffect, useState } from 'react'
import { Button, Checkbox, Empty, Form, Input, Select, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { DataType } from '../../../constants/DataType'
import useWindowSize from '../../../hook/useWindowSize'
import MainTable from '../../../components/Basic/MainTable'
import useParams from '../../../hook/useParams'
import { FormButton } from '../dataSource.styled'
import { useLocation } from 'react-router-dom'
import { CopyOutlined } from '@ant-design/icons'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { Sources } from '../../../constants/Sources'
import { generateSchema } from '../../../apis/genbi'

function TableSetting (props) {
  const { defPlaceholder, defConfig, editMode, tables, tableData, goBack, loading, form, uploadedSample = [], setTableData, showSchemaManagement } = props
  const { t: ln_t } = useTranslation()
  const formVal = Form.useWatch() || {}
  const location = useLocation()
  const { data } = location?.state || {}
  const [generating, setGenerating] = useState(false)
  const [warn, setWarn] = useState([])

  const find = (title, name) => tableData[title]?.find(i => i.columnName === name)
  const handleChangeTable = (title, name, key, value, shouldWarn) => {
    const temp = [...tableData[title]] || []
    temp.find(i => i.columnName === name)[key] = value
    setTableData({ ...tableData, [title]: temp })
    if (shouldWarn) {
      const warnActive = warn.includes(name)
      if (!value && !warnActive) setWarn([...warn, name])
      if (value && warnActive) setWarn(warn?.filter(i => i !== name))
    }
  }

  const handleGenerateSchema = async () => {
    if (!await form.validateFields(['name'], true)) return
    setGenerating(true)
    const payload = {
      name: formVal.name,
      columns: Object.values(tableData || {}).flat().map(i => i.columnName),
      ...formVal.uploadSampleData && { sample: uploadedSample?.map(i => Object.values(i)?.join(', ')) }
    }
    const res = await generateSchema(payload)
    if (res?.status === 200) {
      const { columns, description } = res?.data || {}
      form.setFieldValue('tableAnnotation', description)
      Object.entries(columns)?.map(([key, val]) => handleChangeTable(Object.keys(tableData)[0], key, 'description', val, true))
    }
    setGenerating(false)
  }

  const tableProps = { ...props, warn }
  return Object.keys(tables).length
    ? <>
        {data?.detailName === 'API' && editMode &&
          <div style={{ display: 'flex', gap: '8px' }}>
            <Form.Item {...defConfig('apiID', 'select')} style={{ width: '100%' }}>
              <Input disabled style={{ color: 'rgba(0,0,0,.6)' }}/>
            </Form.Item>
            <CopyToClipboard text={formVal.apiID}>
              <Button icon={<CopyOutlined />} style={{ margin: 'auto 0 24px' }} onClick={() => message.success(ln_t('message.success.copied'))}/>
            </CopyToClipboard>
          </div>}
        {formVal.isIncremental && (formVal.incrementalType === 'append' || formVal.incrementalType === 'snapshot')
          ? <Form.Item {...formVal.incrementalType === 'append' ? defConfig('incrementColumn', 'select') : defConfig('dataIdentificationColumn', 'select')} name="incrementColumn">
              <Select
                showSearch
                placeholder={formVal.incrementalType === 'append' ? defPlaceholder('incrementColumn', 'select') : defPlaceholder('dataIdentificationColumn', 'select')}
                options={Object.values(tableData)?.flat()?.map(({ columnName }) => ({ value: columnName, label: columnName }))}
                optionFilterProp="children"
                disabled={editMode}
              />
            </Form.Item>
          : ''}
        {showSchemaManagement && <>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>{ln_t('dataSource.schemaDescriptionManagement')}</div>
            <Button type='primary' style={{ margin: '0 12px 8px 0' }} size='small' onClick={handleGenerateSchema} loading={generating}>
              {ln_t('dataSource.generateDataSchema')}
            </Button>
            <Form.Item noStyle name="uploadSampleData" valuePropName='checked' initialValue={true}>
              <Checkbox>{ln_t('dataSource.uploadSampleData')}</Checkbox>
            </Form.Item>
            <Form.Item {...defConfig('tableAnnotation', 'input', false)}>
              <Input placeholder={defPlaceholder('tableAnnotation', 'input')}/>
            </Form.Item>
          </>}
        {tables?.map(i => <TableComponent
          {...tableProps}
          {...i}
          key={i.title}
          find={(...args) => find(i.title, ...args)}
          handleChange={(...args) => handleChangeTable(i.title, ...args)}
        />)}

        <FormButton>
          <Button type="default" onClick={goBack}>{ln_t('util.cancel')}</Button>
          <Button type="primary" htmlType="submit" loading={loading}>{ln_t('util.confirm')}</Button>
        </FormButton>
      </>
    : <Empty className="mt-5" description={<><div>{ln_t('dataSource.noUploadedFile')}</div><div>{ln_t('dataSource.upladFirstToSetup')}</div></>} />
}

function TableComponent (props) {
  const { mappingOptions, defPlaceholder, title, tableApi, titleToRender, tableData, conf, editMode, showSchemaManagement, find, handleChange, warn } = props
  const { t: ln_t } = useTranslation()
  const params = useParams()
  const { handleFetchParams } = params
  const { height } = useWindowSize()
  const formVal = Form.useWatch() || {}
  const { STR, INT, FLOAT, DATETIME, DATE } = DataType
  const columnType = [STR, INT, FLOAT, DATE, DATETIME]
  const { changeableType } = conf[formVal.source] || {}

  const columns = [
    { title: ln_t('util.name'), dataIndex: 'columnName' },
    {
      title: ln_t('util.type'),
      dataIndex: 'columnName',
      render: (name, { type }) => changeableType
        ? <Select
              style={{ width: '95%' }}
              placeholder={defPlaceholder('type', 'select')}
              options={columnType?.map(i => ({ value: i, label: i }))}
              onChange={e => handleChange(name, 'type', e)}
              value={find(name)?.type}
              disabled={editMode}
            />
        : type
    },
    {
      title: ln_t('dataSource.alias'),
      dataIndex: 'columnName',
      render: (name) => <Input
        value={find(name)?.alias}
        defaultValue={name}
        status={warn.includes(name) ? 'error' : ''}
        disabled={formVal.source !== Sources.FIRST_PARTY}
        onChange={e => handleChange(name, 'alias', e?.target?.value, true)} />
    },
    {
      title: ln_t('dataSource.pii'),
      dataIndex: 'columnName',
      align: 'center',
      render: name => <Checkbox checked={find(name)?.isPII} onChange={e => handleChange(name, 'isPII', e?.target?.checked)} />
    },
    {
      title: ln_t('dataSource.mappingKey'),
      dataIndex: 'columnName',
      align: 'center',
      render: name => <Checkbox checked={find(name)?.mappingKey} onChange={e => handleChange(name, 'mappingKey', e?.target?.checked)} />
    },
    {
      title: ln_t('dataSource.mappingWith'),
      dataIndex: 'columnName',
      render: name => <Select
          style={{ width: '95%' }}
          placeholder={defPlaceholder('value', 'select')}
          disabled={!find(name)?.mappingKey}
          options={mappingOptions?.map(({ id: value, name: label }) => ({ value, label }))}
          onChange={e => handleChange(name, 'mappingKeyId', e)}
          value={find(name)?.mappingKeyId}
        />
    },
    showSchemaManagement && {
      title: ln_t('dataSource.description'),
      dataIndex: 'columnName',
      render: (name) => <Input value={find(name)?.description} onChange={e => handleChange(name, 'description', e?.target?.value)} />
    }
  ]

  useEffect(() => { handleFetchParams('add', {}) }, [tableData])

  return (
    <div key={title} style={{ paddingBottom: '24px' }}>
      <div style={{ fontWeight: 500 }}>{titleToRender || title}</div>
      <MainTable
        columns={columns}
        rowKey="columnName"
        apiFunc={tableApi}
        params={params}
        scrollY={height - 400}
        noPage={true}
      />
    </div>
  )
}

export default TableSetting
